import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Flex, Select } from 'antd';
import styled from 'styled-components';
import { NoContentItem } from '../../../../common/components';
import { FormField, FormInputMessages } from '../../../../common/form';
import {
  AlgorithmSourceDimensionFormDataType,
  AlgorithmSourceFormDataType,
  AlgorithmSourcesConfidentialityRuleSourceValidFormDataType,
  AlgorithmSourcesValidFormDataType,
} from '../schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { createAlgorithmSourceDimensionsOptionGroup } from '../utils';
import translations from './AlgorithmConfidentialityRuleSourceDimensionFormField.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-confidentiality-rule-source-dimension-form-field',
);
addTranslations(translations);

export const AlgorithmConfidentialityRuleSourceDimensionFormField: FC = () => {
  const { t } = useTranslation();
  const sources = useWatch<AlgorithmSourcesValidFormDataType, 'sources'>({
    name: 'sources',
  });
  const { getValues, setValue } =
    useFormContext<AlgorithmSourcesValidFormDataType>();

  const handleChange = useCallback(
    (
      value: string,
      option: {
        dimension: AlgorithmSourceDimensionFormDataType;
        algorithmSource: AlgorithmSourceFormDataType;
        sourceIndex: number;
      },
    ) => {
      setSelectedValue(value);
      setValue('confidentialityRule.source', {
        dimension: option.dimension.code,
        index: option.sourceIndex,
      });
    },
    [setValue],
  );

  const clearValue = useCallback(() => {
    setSelectedValue('');
    setValue('confidentialityRule.source', null);
  }, [setValue]);

  // Clear selected value if the source of currently selected dimension is removed
  const sourcesRef = useRef(sources);
  useEffect(() => {
    if (sources.length < sourcesRef.current.length) {
      const removedIndex = sourcesRef.current.findIndex(
        (source) => !sources.includes(source),
      );
      if (
        removedIndex !== -1 &&
        removedIndex === getValues('confidentialityRule.source.index')
      ) {
        clearValue();
      }
    }
    sourcesRef.current = sources;
  }, [sources.length, getValues, clearValue]);

  const optionGroups = useMemo(
    () =>
      sources.map((s, i) => createAlgorithmSourceDimensionsOptionGroup(s, i)),
    [sources],
  );

  const [selectedValue, setSelectedValue] = useState<string | null>(() =>
    findOptionValueBySource(
      optionGroups,
      getValues('confidentialityRule.source'),
    ),
  );

  useAutoSelectSubjectDimensionOnSelectingNewSource(
    useCallback(
      (source) =>
        setSelectedValue(findOptionValueBySource(optionGroups, source)),
      [optionGroups],
    ),
  );

  return (
    <FormField
      name="confidentialityRule.source.dimension"
      label={t('sourceDimension.label')}
      required
    >
      <Flex vertical gap="small">
        <StyledSelect
          id="confidentiality-rule-source-dimension"
          options={optionGroups}
          allowClear
          optionLabelProp="displayLabel"
          value={selectedValue}
          onSelect={handleChange}
          onClear={clearValue}
          styles={{
            popup: { root: { width: 'max-content' } },
          }}
          notFoundContent={<NoContentItem />}
        />
        <FormInputMessages name="confidentialityRule.source" />
      </Flex>
    </FormField>
  );
};

const findOptionValueBySource = (
  optionGroups: {
    options: {
      value: string;
      dimension: AlgorithmSourceDimensionFormDataType;
    }[];
  }[],
  source?: AlgorithmSourcesConfidentialityRuleSourceValidFormDataType | null,
) => {
  if (!source) {
    return null;
  }

  const { options } = optionGroups[source.index];
  if (!options.length) {
    return source.dimension;
  }
  const { value } = options.find(
    ({ dimension }) => dimension.code === source.dimension,
  )!;

  return value;
};

const StyledSelect = styled(Select<string, any>)`
  height: auto;
`;

const useAutoSelectSubjectDimensionOnSelectingNewSource = (
  onAutoSelect: (
    confidentialityRuleSource: AlgorithmSourcesConfidentialityRuleSourceValidFormDataType,
  ) => unknown,
) => {
  const sources = useWatch<AlgorithmSourcesValidFormDataType, 'sources'>({
    name: 'sources',
  });
  const { getValues, setValue } =
    useFormContext<AlgorithmSourcesValidFormDataType>();

  const sourcesCountRef = useRef(sources.length);
  const watchingLastSourceRef = useRef(false);

  useEffect(() => {
    const sourceDimension = getValues('confidentialityRule.source.dimension');

    if (!sourceDimension) {
      const wasSourceAdded = sources.length > sourcesCountRef.current;

      if (watchingLastSourceRef.current) {
        const sourceIndex = sources.length - 1;
        const { sourceId, dimensions } = sources[sourceIndex];
        const isFilled = Boolean(sourceId);

        if (isFilled) {
          watchingLastSourceRef.current = false;

          if (dimensions.some(({ code }) => code === 'SUBJEKT')) {
            const confidentialityRuleSource = {
              dimension: 'SUBJEKT',
              index: sourceIndex,
            };
            setValue('confidentialityRule.source', confidentialityRuleSource);
            onAutoSelect(confidentialityRuleSource);
          }
        }
      } else if (wasSourceAdded) {
        watchingLastSourceRef.current = true;
      }
    }

    sourcesCountRef.current = sources.length;
  }, [sources, getValues, setValue, onAutoSelect]);
};
